import type { MLTripSummary, StopSummary } from "@/features/towers/types";
import {
  type DeliveryBenchmarkLane,
  isStopEligibleForBenchmark,
} from "@/features/towers/utils/deliveryBenchmark";
import { hasRecordedStopArrival } from "@/features/towers/utils/tripLatestArrival";
import {
  getVisitStatusForStopBenchmark,
  type VisitStatus,
} from "@/features/towers/utils/visitStatus";

export function tripHasPredictedNextDelivery(trip: MLTripSummary): boolean {
  const nextClusterId = trip.predictedNextStopClusterId;
  return (
    trip.currentStage === "IN_TRANSIT" &&
    nextClusterId != null &&
    nextClusterId !== ""
  );
}

function getEligibleStopsAtPredictedNextCluster(
  trip: MLTripSummary,
): StopSummary[] {
  const nextClusterId = trip.predictedNextStopClusterId;
  if (nextClusterId == null || nextClusterId === "") {
    return [];
  }

  return trip.stopSummaries.filter(
    (stop) =>
      isStopEligibleForBenchmark(stop) && stop.clusterId === nextClusterId,
  );
}

function isUpcomingStopSummary(stop: StopSummary): boolean {
  return !hasRecordedStopArrival(stop);
}

export function getNextDeliveryStopSummary(
  trip: MLTripSummary,
): StopSummary | undefined {
  return getEligibleStopsAtPredictedNextCluster(trip).find(
    isUpcomingStopSummary,
  );
}

/** Hollow next-delivery dot when ETA predicts a next cluster and it is not fully arrived in stopSummaries. */
export function shouldShowNextDeliveryHollowDot(trip: MLTripSummary): boolean {
  if (!tripHasPredictedNextDelivery(trip)) {
    return false;
  }

  const matchingStops = getEligibleStopsAtPredictedNextCluster(trip);
  if (matchingStops.length === 0) {
    return true;
  }

  return matchingStops.some(isUpcomingStopSummary);
}

export function getNextDeliveryVisitStatusForTrip(
  trip: MLTripSummary,
  lane: DeliveryBenchmarkLane,
): VisitStatus {
  const nextStop = getNextDeliveryStopSummary(trip);
  if (nextStop == null) {
    return "UNKNOWN";
  }
  return getVisitStatusForStopBenchmark(nextStop, lane) ?? "UNKNOWN";
}
